import React, { useState, useEffect, useRef } from 'react'
import { Send, Plus, Palette, X, Paperclip, Check, ShoppingBag, Sparkles, FileText, Share2, ChevronDown, Link2, Search } from 'lucide-react'
import { BrandKit } from '../../types/brandKit'
import { BrandKitPicker } from './BrandKitPicker'

/** Per-file cap for image attachments (matches AICreativeModal behaviour). */
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024
/** Hard cap on number of attachments per message. */
const MAX_ATTACHMENTS = 10

export type WorkflowKey = 'agentic_buy' | 'ai_creative_generation'

/**
 * `social_ad` is intentionally *not* a `WorkflowKey`. The chip toggles a
 * local input panel (URL / sizes / provider) that the user fills to shape
 * the outbound message, but the resulting query is still sent as a plain
 * natural-language message without any `workflow` tag — the BE picks up
 * the intent from the composed sentence, matching the "nothing changes
 * on the wire" contract for this feature.
 */
export type ChipKey = WorkflowKey | 'social_ad'

export interface WorkflowOption {
  key: ChipKey
  label: string
  icon: React.ReactNode
}

export const DEFAULT_WORKFLOW_OPTIONS: WorkflowOption[] = [
  { key: 'agentic_buy', label: 'Agentic Buy', icon: <ShoppingBag className="w-4 h-4" /> },
  { key: 'ai_creative_generation', label: 'Creative Generation', icon: <Sparkles className="w-4 h-4" /> },
  { key: 'social_ad', label: 'Social Ad', icon: <Share2 className="w-4 h-4" /> },
]

/**
 * Options for the Social Ad size multi-select. Grouped so IAB display
 * sizes, social-native presets and responsive/custom entries stay
 * visually distinct in the dropdown. Custom entries are appended at
 * runtime via component state (see `socialAdCustomSizes`).
 */
const SOCIAL_AD_SIZE_GROUPS: Array<{ label: string; sizes: string[] }> = [
  {
    label: 'Responsive',
    sizes: ['Responsive'],
  },
  {
    label: 'Social presets',
    sizes: [
      '1080x1080 (1:1 Square)',
      '1080x1920 (9:16 Story/Reel)',
      '1080x1350 (4:5 Portrait)',
      '1200x628 (16:9 Landscape)',
    ],
  },
  {
    label: 'IAB standard',
    sizes: [
      '300x250',
      '728x90',
      '300x600',
      '320x50',
      '970x250',
      '160x600',
      '336x280',
      '468x60',
    ],
  },
]

/** Social provider single-select options. */
const SOCIAL_AD_PROVIDERS = [
  'Instagram',
  'Facebook',
  'TikTok',
  'LinkedIn',
  'X (Twitter)',
  'YouTube',
  'Pinterest',
  'Snapchat',
] as const

interface PromptInputProps {
  onSubmit: (data: any) => void
  isLoading: boolean
  initialData?: any
  suggestionText?: string
  selectedBrandKit?: BrandKit | null
  onSelectBrandKit?: (bk: BrandKit | null) => void
  brandKitAdvertiserId?: string
  showBrandKitOption?: boolean
  showWorkflowOptions?: boolean
  workflowOptions?: WorkflowOption[]
  /**
   * Non-editable context chip rendered above the textarea. Used by the
   * concept picker's "Refine" action: instead of preseeding text the
   * user could delete or garble, we surface the target concept as a
   * pill (`Refine 'Aspirational Journey' (c3)`) with an × to clear it.
   * The user only types their tweak; the parent is responsible for
   * composing the final outbound query from the chip label + typed
   * text. Passing `null`/`undefined` hides the chip entirely.
   */
  attachedContext?: {
    label: string
    onClear: () => void
  } | null
}

export function PromptInput({
  onSubmit,
  isLoading,
  initialData,
  suggestionText,
  selectedBrandKit,
  onSelectBrandKit,
  brandKitAdvertiserId,
  showBrandKitOption = false,
  showWorkflowOptions = false,
  workflowOptions = DEFAULT_WORKFLOW_OPTIONS,
  attachedContext = null,
}: PromptInputProps) {
  const [objective, setObjective] = useState(initialData?.objective || '')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuDirection, setMenuDirection] = useState<'up' | 'down'>('down')
  const [isBrandKitPickerOpen, setIsBrandKitPickerOpen] = useState(false)
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowKey | null>(null)
  // Independent of `selectedWorkflow`: the Social Ad chip is UI-only and
  // does *not* set a workflow on the wire. When true, we render the
  // inline social-ad panel above the send bar and compose the outbound
  // message from its fields on submit.
  const [isSocialAdActive, setIsSocialAdActive] = useState(false)
  const [socialAdUrl, setSocialAdUrl] = useState('')
  const [socialAdSizes, setSocialAdSizes] = useState<string[]>([])
  // User-defined `WxH` sizes added via the inline row in the size
  // dropdown. Kept separate from `socialAdSizes` (which stores the
  // selected checkboxes) because custom sizes should always be
  // treated as selected: if a user bothered to type it, they want it
  // included. On submit we merge both lists.
  const [socialAdCustomSizes, setSocialAdCustomSizes] = useState<string[]>([])
  const [customSizeW, setCustomSizeW] = useState('')
  const [customSizeH, setCustomSizeH] = useState('')
  const [socialAdProvider, setSocialAdProvider] = useState<string>('')
  const [isSocialSizeOpen, setIsSocialSizeOpen] = useState(false)
  const [isSocialProviderOpen, setIsSocialProviderOpen] = useState(false)
  // Filter query for the size dropdown. Matches against both the raw
  // dimension (e.g. "1080x1080") and the annotated label (e.g. "1:1
  // Square") so users can search by either. Empty string → show all.
  const [sizeSearch, setSizeSearch] = useState('')
  const socialSizeRef = useRef<HTMLDivElement>(null)
  const socialProviderRef = useRef<HTMLDivElement>(null)
  // Reference assets the user wants to attach to this message. Only kept in
  // local state — Home.tsx forwards them as FormData on submit and we clear
  // immediately after, so nothing persists across messages.
  const [attachments, setAttachments] = useState<File[]>([])
  const [attachmentPreviews, setAttachmentPreviews] = useState<string[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const plusBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (initialData?.objective) setObjective(initialData.objective)
  }, [initialData])

  useEffect(() => {
    if (suggestionText) {
      setObjective(suggestionText)
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(suggestionText.length, suggestionText.length)
      }
    }
  }, [suggestionText])

  // Focus the textarea whenever a new attached-context chip appears so
  // the user can immediately start typing their refinement without
  // clicking into the input. Keyed on object identity (not just label)
  // so re-clicking Refine on the same card — which produces a fresh
  // wrapper object with identical text — still retriggers the focus.
  useEffect(() => {
    if (attachedContext && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [attachedContext])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
    }
  }, [objective])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false)
      }
    }
    if (isMenuOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  // Close the Social Ad size + provider dropdowns when the user clicks
  // anywhere outside them. Kept separate from the Plus menu closer above
  // so each popover manages only its own outside-click surface.
  useEffect(() => {
    if (!isSocialSizeOpen && !isSocialProviderOpen) return
    const handle = (e: MouseEvent) => {
      const target = e.target as Node
      if (isSocialSizeOpen && socialSizeRef.current && !socialSizeRef.current.contains(target)) {
        setIsSocialSizeOpen(false)
      }
      if (isSocialProviderOpen && socialProviderRef.current && !socialProviderRef.current.contains(target)) {
        setIsSocialProviderOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [isSocialSizeOpen, isSocialProviderOpen])

  // Strips the "(1:1 Square)" annotation from a size preset label so the
  // outbound sentence contains just the raw dimensions the BE cares about.
  const rawSize = (label: string): string => label.split(' ')[0]

  /**
   * URL validation for the Social Ad post field.
   *
   * We can't rely on the browser's native `type="url"` + `required`
   * constraint validation here: the textarea's `onKeyDown` Enter
   * handler calls `handleSubmit(e)` imperatively and bypasses the
   * form's native submit event, so any malformed value in the URL
   * input silently flows through `composeSocialAdMessage` when Enter
   * is pressed from the textarea. Meanwhile clicking Send goes through
   * native submit and *is* blocked. To make both paths behave the
   * same we validate explicitly in JS via `canSubmit()`.
   *
   * We require both a parseable URL *and* an http/https protocol so
   * `javascript:...`, `data:...`, etc. can't sneak through — the BE
   * only ever cares about web URLs for social posts.
   */
  const isValidUrl = (raw: string): boolean => {
    const trimmed = raw.trim()
    if (!trimmed) return false
    try {
      const parsed = new URL(trimmed)
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
      return false
    }
  }

  const canSubmit = (): boolean => {
    if (isLoading) return false
    if (isSocialAdActive) {
      // Social Ad mode: the URL is the only required field, but it
      // must also be a well-formed http(s) URL. Typed objective is
      // optional — we synthesise the message from the panel fields.
      return isValidUrl(socialAdUrl)
    }
    return objective.trim().length > 0
  }

  // Only surface the inline "not a valid URL" error once the user has
  // typed something — an empty field on first render shouldn't scream
  // red at them. The disabled Send button + tooltip cover the empty
  // case separately.
  const showUrlError =
    isSocialAdActive && socialAdUrl.trim().length > 0 && !isValidUrl(socialAdUrl)

  const composeSocialAdMessage = (): string => {
    const parts: string[] = []
    parts.push(`Create a social ad from ${socialAdUrl.trim()}.`)
    // Merge preset selections with any custom W×H sizes the user
    // typed. Preset labels like "1080x1080 (1:1 Square)" are stripped
    // to their raw dimensions; the string "Responsive" passes through
    // untouched so the BE sees the intent verbatim.
    // Dedupe across presets + custom: a user can e.g. tick the
    // `300x250` IAB preset AND type it in the custom W×H row, which
    // would otherwise produce `Sizes: 300x250, 300x250.` in the
    // outbound sentence — corrupting the natural-language contract
    // the BE parses. `Set` preserves first-insertion order so preset
    // ticks still lead custom entries.
    const allSizes = Array.from(
      new Set<string>([...socialAdSizes.map(rawSize), ...socialAdCustomSizes]),
    )
    if (allSizes.length > 0) {
      parts.push(`Sizes: ${allSizes.join(', ')}.`)
    }
    if (socialAdProvider) {
      parts.push(`Provider: ${socialAdProvider}.`)
    }
    const typed = objective.trim()
    if (typed) parts.push(typed)
    return parts.join(' ')
  }

  const addCustomSize = () => {
    const w = parseInt(customSizeW, 10)
    const h = parseInt(customSizeH, 10)
    // Loose validation per spec: positive integers only. Anything
    // non-numeric or ≤0 is silently ignored so a stray Enter on
    // empty inputs doesn't add "NaNxNaN" to the list.
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return
    const key = `${w}x${h}`
    // Cross-source dedupe: if the same dimension already exists as a
    // ticked preset (e.g. "300x250" IAB) we no-op. `composeSocialAdMessage`
    // dedupes on submit too, but without this the trigger would render
    // two chips (one preset-styled, one custom-styled) for what is a
    // single actual selection — visually misleading. Clear inputs
    // regardless so the row stays responsive.
    const alreadyPreset = socialAdSizes.some((p) => rawSize(p) === key)
    if (!alreadyPreset) {
      setSocialAdCustomSizes((prev) => (prev.includes(key) ? prev : [...prev, key]))
    }
    setCustomSizeW('')
    setCustomSizeH('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit()) return

    // Social Ad chip is UI-only: compose a natural-language query from
    // the panel fields and send it through the standard path with NO
    // `workflow` set (per product spec — the BE infers intent from the
    // sentence itself).
    const outbound = isSocialAdActive ? composeSocialAdMessage() : objective.trim()

    onSubmit({
      objective: outbound,
      ...(selectedWorkflow ? { workflow: selectedWorkflow } : {}),
      ...(attachments.length > 0 ? { attachments: [...attachments] } : {}),
    })
    setObjective('')
    setSelectedWorkflow(null)
    // Reset the social-ad panel too so the next message starts clean.
    setIsSocialAdActive(false)
    setSocialAdUrl('')
    setSocialAdSizes([])
    setSocialAdCustomSizes([])
    setCustomSizeW('')
    setCustomSizeH('')
    setSizeSearch('')
    setSocialAdProvider('')
    // Clear attachments immediately so the next message starts clean. The
    // file objects themselves are now owned by the parent (forwarded via
    // FormData) so we don't keep references here.
    attachmentPreviews.forEach(u => URL.revokeObjectURL(u))
    setAttachments([])
    setAttachmentPreviews([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileUpload = () => {
    setIsMenuOpen(false)
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files || [])
    if (incoming.length === 0) return

    // Filter out files that would breach the per-file size cap; we surface a
    // single console warning rather than blocking the rest of the batch.
    const accepted: File[] = []
    let rejected = 0
    for (const f of incoming) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        rejected += 1
        continue
      }
      accepted.push(f)
    }
    if (rejected > 0) {
      console.warn(`PromptInput: skipped ${rejected} file(s) larger than 15MB`)
    }

    setAttachments(prev => {
      const merged = [...prev, ...accepted].slice(0, MAX_ATTACHMENTS)
      return merged
    })
    setAttachmentPreviews(prev => {
      const newPreviews = accepted.map(f =>
        f.type.startsWith('image/') ? URL.createObjectURL(f) : '',
      )
      const merged = [...prev, ...newPreviews].slice(0, MAX_ATTACHMENTS)
      // Revoke any URLs that fell off the tail (very unlikely with a 10-cap
      // but cheap to be safe).
      const dropped = [...prev, ...newPreviews].slice(MAX_ATTACHMENTS)
      dropped.forEach(u => u && URL.revokeObjectURL(u))
      return merged
    })

    // Reset the input so selecting the same file twice still triggers
    // change (browsers suppress events when the value is identical).
    e.target.value = ''
  }

  const removeAttachment = (idx: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx))
    setAttachmentPreviews(prev => {
      const url = prev[idx]
      if (url) URL.revokeObjectURL(url)
      return prev.filter((_, i) => i !== idx)
    })
  }

  // Revoke any pending object URLs on unmount.
  useEffect(() => {
    return () => {
      attachmentPreviews.forEach(u => u && URL.revokeObjectURL(u))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBrandKitMenuClick = () => {
    setIsMenuOpen(false)
    setIsBrandKitPickerOpen((prev) => !prev)
  }

  const hasBrandKit = !!selectedBrandKit

  return (
    <div className="w-full space-y-3">
      <form onSubmit={handleSubmit}>
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-300 dark:border-gray-700 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 transition-all overflow-visible">
          {/* Attached-context chip. Rendered as a non-editable pill so
              e.g. "Refine 'Card Title' (c3)" stays intact no matter
              what the user types below — it's metadata, not text.
              Sits inside the same rounded frame as the textarea so
              the pill visually anchors to the input it modifies. */}
          {attachedContext && (
            <div className="px-3 pt-2.5">
              <span
                className="inline-flex items-center gap-1.5 max-w-full pl-2.5 pr-1 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 text-xs font-semibold"
                title={attachedContext.label}
              >
                <span className="truncate max-w-[240px]">{attachedContext.label}</span>
                <button
                  type="button"
                  onClick={attachedContext.onClear}
                  className="flex-shrink-0 w-4 h-4 rounded-full hover:bg-primary-100 dark:hover:bg-primary-800 text-primary-600 dark:text-primary-300 flex items-center justify-center"
                  aria-label="Clear refine target"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            </div>
          )}
          {/* Social Ad inline panel — rendered inside the prompt frame so
              it feels like part of the composer, not a separate widget.
              URL is required; sizes and provider are optional. When
              active, the textarea below becomes "additional notes"
              (still optional) rather than the primary input, and Send is
              gated on the URL field via `canSubmit`. */}
          {isSocialAdActive && (
            <div className="px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-800 space-y-2.5">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-200">
                <Share2 className="w-3.5 h-3.5 text-primary-500" />
                <span>Social Ad</span>
                <span className="text-[10px] font-normal text-gray-400 dark:text-gray-500">
                  Paste the post URL to generate an ad from it
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setIsSocialAdActive(false)
                    setSocialAdUrl('')
                    setSocialAdSizes([])
                    setSocialAdCustomSizes([])
                    setCustomSizeW('')
                    setCustomSizeH('')
                    setSizeSearch('')
                    setSocialAdProvider('')
                  }}
                  className="ml-auto w-5 h-5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
                  aria-label="Close Social Ad panel"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>

              {/* Required URL field */}
              <div className="relative">
                <Link2
                  className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
                    showUrlError ? 'text-red-400' : 'text-gray-400'
                  }`}
                />
                {/*
                  Explicit `type="text"` (not `type="url"`) + no
                  `required` attribute: we validate via `isValidUrl`
                  in `canSubmit()` so the textarea-Enter path (which
                  bypasses native form submission) and the Send-click
                  path give consistent feedback. Native constraint
                  validation would only fire on one of those two paths
                  and would surface a browser bubble instead of our
                  inline error.
                */}
                <input
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  value={socialAdUrl}
                  onChange={(e) => setSocialAdUrl(e.target.value)}
                  placeholder="https://instagram.com/p/... (required)"
                  aria-label="Social post URL"
                  aria-invalid={showUrlError}
                  aria-describedby={showUrlError ? 'social-ad-url-error' : undefined}
                  className={`w-full pl-8 pr-3 py-2 text-sm rounded-lg border bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    showUrlError
                      ? 'border-red-400 dark:border-red-500 focus:ring-red-500/30 focus:border-red-500'
                      : 'border-gray-200 dark:border-gray-800 focus:ring-primary-500/40 focus:border-primary-500'
                  }`}
                />
              </div>
              {showUrlError && (
                <p
                  id="social-ad-url-error"
                  className="text-[11px] text-red-500 dark:text-red-400 -mt-1"
                >
                  Enter a valid http(s) URL, e.g. https://instagram.com/p/…
                </p>
              )}

              {/* Sizes multi-select + Provider single-select. Two
                  popovers side-by-side; each is its own click-outside
                  surface (see the useEffect above). */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Sizes */}
                <div ref={socialSizeRef} className="relative">
                  {/*
                    Trigger button doubles as a chip container. When
                    no sizes are picked we show the placeholder text,
                    otherwise we render the selection inline as small
                    removable pills — that way the user can see (and
                    prune) their picks without opening the dropdown.
                    Chips call `stopPropagation` so clicking their ×
                    removes the size without also toggling the
                    dropdown open/closed.
                  */}
                  {/*
                    Using a <div role="button"> instead of <button> so
                    the removable chips inside stay valid interactive
                    children — HTML disallows nesting <button> inside
                    <button>, and even with stopPropagation the nested
                    form of the previous version could surface the
                    outer click in some browsers. Enter/Space/Down on
                    the container toggles the dropdown just like a
                    real button would.
                  */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-haspopup="listbox"
                    aria-expanded={isSocialSizeOpen}
                    onClick={() => {
                      setIsSocialSizeOpen((v) => !v)
                      setIsSocialProviderOpen(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                        e.preventDefault()
                        setIsSocialSizeOpen((v) => !v)
                        setIsSocialProviderOpen(false)
                      } else if (e.key === 'Escape' && isSocialSizeOpen) {
                        e.preventDefault()
                        setIsSocialSizeOpen(false)
                      }
                    }}
                    className="w-full min-h-[34px] flex items-center justify-between gap-2 pl-1.5 pr-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-700 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                  >
                    {(() => {
                      const total = socialAdSizes.length + socialAdCustomSizes.length
                      if (total === 0) {
                        return (
                          <span className="pl-1.5 text-gray-500 dark:text-gray-400">
                            Sizes (optional)
                          </span>
                        )
                      }
                      return (
                        <span className="flex flex-wrap items-center gap-1 min-w-0">
                          {socialAdSizes.map((size) => (
                            <span
                              key={`preset-${size}`}
                              className="inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded-full text-[10px] font-medium bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300"
                              title={size}
                            >
                              <span className="max-w-[110px] truncate">
                                {rawSize(size)}
                              </span>
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label={`Remove ${size}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSocialAdSizes((prev) =>
                                    prev.filter((s) => s !== size),
                                  )
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setSocialAdSizes((prev) =>
                                      prev.filter((s) => s !== size),
                                    )
                                  }
                                }}
                                className="w-3.5 h-3.5 rounded-full hover:bg-primary-100 dark:hover:bg-primary-800 flex items-center justify-center cursor-pointer"
                              >
                                <X className="w-2 h-2" />
                              </span>
                            </span>
                          ))}
                          {socialAdCustomSizes.map((size) => (
                            <span
                              key={`custom-${size}`}
                              className="inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                              title={`${size} (custom)`}
                            >
                              <span className="max-w-[110px] truncate">{size}</span>
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label={`Remove ${size}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSocialAdCustomSizes((prev) =>
                                    prev.filter((s) => s !== size),
                                  )
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setSocialAdCustomSizes((prev) =>
                                      prev.filter((s) => s !== size),
                                    )
                                  }
                                }}
                                className="w-3.5 h-3.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center cursor-pointer"
                              >
                                <X className="w-2 h-2" />
                              </span>
                            </span>
                          ))}
                        </span>
                      )
                    })()}
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${
                        isSocialSizeOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                  {isSocialSizeOpen && (
                    <div
                      className="absolute left-0 right-0 top-full mt-1 z-[60] max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-lg"
                      role="listbox"
                    >
                      {/* Sticky top: Custom size add row + selected
                          custom chips, then a search filter for the
                          preset lists below. Both stay pinned so the
                          user can keep typing while scrolling long
                          preset lists — the Custom section was hard
                          to reach when it lived at the bottom. */}
                      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                          Custom
                        </div>
                        <div className="px-3 pb-2 flex items-center gap-1.5">
                          <input
                            type="number"
                            min={1}
                            value={customSizeW}
                            onChange={(e) => setCustomSizeW(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                addCustomSize()
                              }
                            }}
                            placeholder="W"
                            className="w-14 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500/40 focus:border-primary-500"
                            aria-label="Custom width"
                          />
                          <span className="text-[10px] text-gray-400">×</span>
                          <input
                            type="number"
                            min={1}
                            value={customSizeH}
                            onChange={(e) => setCustomSizeH(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                addCustomSize()
                              }
                            }}
                            placeholder="H"
                            className="w-14 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500/40 focus:border-primary-500"
                            aria-label="Custom height"
                          />
                          <button
                            type="button"
                            onClick={addCustomSize}
                            disabled={
                              !(
                                Number(customSizeW) > 0 && Number(customSizeH) > 0
                              )
                            }
                            className="ml-auto px-2.5 py-1 text-[11px] font-medium rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Add
                          </button>
                        </div>
                        {socialAdCustomSizes.length > 0 && (
                          <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                            {socialAdCustomSizes.map((size) => (
                              <span
                                key={size}
                                className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] font-medium bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300"
                              >
                                {size}
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSocialAdCustomSizes((prev) =>
                                      prev.filter((s) => s !== size),
                                    )
                                  }
                                  className="w-3.5 h-3.5 rounded-full hover:bg-primary-100 dark:hover:bg-primary-800 flex items-center justify-center"
                                  aria-label={`Remove ${size}`}
                                >
                                  <X className="w-2 h-2" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Search filter for the preset lists below.
                            Matches case-insensitive substring against
                            each option string, so "1:1", "square",
                            "300x", "story" all narrow the list. */}
                        <div className="px-2 pb-2 relative">
                          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                          <input
                            type="text"
                            value={sizeSearch}
                            onChange={(e) => setSizeSearch(e.target.value)}
                            onKeyDown={(e) => {
                              // The size dropdown lives inside the
                              // main <form>. Without this guard,
                              // pressing Enter here would trigger
                              // implicit form submission and send
                              // the composed Social Ad message even
                              // though the user only meant to filter
                              // the list. Matches the same guard on
                              // the custom W/H inputs above.
                              if (e.key === 'Enter') {
                                e.preventDefault()
                              } else if (e.key === 'Escape') {
                                if (sizeSearch) {
                                  e.preventDefault()
                                  setSizeSearch('')
                                }
                              }
                            }}
                            placeholder="Search sizes…"
                            className="w-full pl-7 pr-6 py-1 text-xs rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500/40 focus:border-primary-500"
                            aria-label="Search sizes"
                          />
                          {sizeSearch && (
                            <button
                              type="button"
                              onClick={() => setSizeSearch('')}
                              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex items-center justify-center"
                              aria-label="Clear search"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Preset groups — filtered by `sizeSearch`.
                          Case-insensitive substring match on the raw
                          option string (which already includes both
                          the dimensions and the annotation, e.g.
                          "1080x1080 (1:1 Square)"). Groups with zero
                          matches are hidden entirely. */}
                      {(() => {
                        const q = sizeSearch.trim().toLowerCase()
                        const filtered = SOCIAL_AD_SIZE_GROUPS.map((group) => ({
                          label: group.label,
                          sizes: q
                            ? group.sizes.filter((s) => s.toLowerCase().includes(q))
                            : group.sizes,
                        })).filter((g) => g.sizes.length > 0)

                        if (filtered.length === 0) {
                          return (
                            <div className="px-3 py-4 text-center text-[11px] text-gray-400 dark:text-gray-500">
                              No sizes match “{sizeSearch}”
                            </div>
                          )
                        }

                        return filtered.map((group) => (
                          <div key={group.label}>
                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                              {group.label}
                            </div>
                            {group.sizes.map((size) => {
                              const checked = socialAdSizes.includes(size)
                              return (
                                <button
                                  key={size}
                                  type="button"
                                  onClick={() => {
                                    setSocialAdSizes((prev) =>
                                      prev.includes(size)
                                        ? prev.filter((s) => s !== size)
                                        : [...prev, size],
                                    )
                                    // Cross-source dedupe: if a custom
                                    // entry already matches this preset's
                                    // raw dimension (e.g. user typed
                                    // 300×250 first, then ticks the
                                    // "300x250" IAB preset), remove the
                                    // custom copy so only one chip
                                    // renders. A toggle-*off* leaves the
                                    // custom list alone — the two
                                    // sources are only merged when the
                                    // preset is active.
                                    if (!checked) {
                                      const key = rawSize(size)
                                      setSocialAdCustomSizes((prev) =>
                                        prev.filter((c) => c !== key),
                                      )
                                    }
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                  role="option"
                                  aria-selected={checked}
                                >
                                  <span
                                    className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                                      checked
                                        ? 'bg-primary-600 border-primary-600 text-white'
                                        : 'border-gray-300 dark:border-gray-600'
                                    }`}
                                  >
                                    {checked && <Check className="w-2.5 h-2.5" />}
                                  </span>
                                  <span className="text-gray-700 dark:text-gray-200">{size}</span>
                                </button>
                              )
                            })}
                          </div>
                        ))
                      })()}
                    </div>
                  )}
                </div>

                {/* Provider */}
                <div ref={socialProviderRef} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSocialProviderOpen((v) => !v)
                      setIsSocialSizeOpen(false)
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
                    aria-haspopup="listbox"
                    aria-expanded={isSocialProviderOpen}
                  >
                    <span className="truncate">
                      {socialAdProvider || 'Provider (optional)'}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${
                        isSocialProviderOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {isSocialProviderOpen && (
                    <div
                      className="absolute left-0 right-0 top-full mt-1 z-[60] max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-lg"
                      role="listbox"
                    >
                      {socialAdProvider && (
                        <button
                          type="button"
                          onClick={() => {
                            setSocialAdProvider('')
                            setIsSocialProviderOpen(false)
                          }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-100 dark:border-gray-800"
                        >
                          <X className="w-3 h-3" />
                          Clear selection
                        </button>
                      )}
                      {SOCIAL_AD_PROVIDERS.map((prov) => {
                        const active = socialAdProvider === prov
                        return (
                          <button
                            key={prov}
                            type="button"
                            onClick={() => {
                              setSocialAdProvider(prov)
                              setIsSocialProviderOpen(false)
                            }}
                            className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            role="option"
                            aria-selected={active}
                          >
                            <span
                              className={`${
                                active
                                  ? 'text-primary-600 dark:text-primary-400 font-medium'
                                  : 'text-gray-700 dark:text-gray-200'
                              }`}
                            >
                              {prov}
                            </span>
                            {active && <Check className="w-3 h-3 text-primary-500" />}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Textarea */}
          <div className="px-4 pt-3 pb-1">
            <textarea
              ref={textareaRef}
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && canSubmit()) {
                  e.preventDefault()
                  handleSubmit(e as any)
                }
              }}
              placeholder={
                isSocialAdActive
                  ? 'Add any extra notes (optional)…'
                  : 'Enter a prompt for AgenX'
              }
              className="w-full border-0 focus:outline-none resize-none text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 bg-transparent min-h-[40px] max-h-[200px] overflow-y-auto"
              style={{ lineHeight: '24px', paddingTop: '8px', paddingBottom: '8px' }}
              rows={2}
              // `required` conflicts with Social Ad mode where the URL is
              // the required field instead of the textarea. Send button
              // + canSubmit() enforce validation for both modes.
              required={!isSocialAdActive}
            />
          </div>

          {/* Attachment strip — shows previews for queued reference files.
              Only rendered when at least one attachment is staged so it
              doesn't take up space in the empty state. */}
          {attachments.length > 0 && (
            <div className="px-3 pb-1 pt-0.5 flex flex-wrap gap-2">
              {attachments.map((file, idx) => {
                const previewUrl = attachmentPreviews[idx]
                const isImage = !!previewUrl && file.type.startsWith('image/')
                return (
                  <div
                    key={`${file.name}-${idx}`}
                    className="relative group inline-flex items-center gap-2 max-w-[220px] pl-1 pr-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60"
                    title={`${file.name} (${Math.round(file.size / 1024)} KB)`}
                  >
                    {isImage ? (
                      <img
                        src={previewUrl}
                        alt={file.name}
                        className="h-7 w-7 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="h-7 w-7 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                      </div>
                    )}
                    <span className="text-xs text-gray-700 dark:text-gray-200 truncate max-w-[140px]">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(idx)}
                      className="flex-shrink-0 w-4 h-4 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 flex items-center justify-center"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Bottom bar */}
          <div className="flex items-center gap-1.5 px-3 pb-3 pt-1">
            {/* Plus button with dropdown */}
            <div ref={menuRef} className="relative">
              <button
                ref={plusBtnRef}
                type="button"
                onClick={() => {
                  if (!isMenuOpen && plusBtnRef.current) {
                    const rect = plusBtnRef.current.getBoundingClientRect()
                    const spaceBelow = window.innerHeight - rect.bottom
                    setMenuDirection(spaceBelow < 160 ? 'up' : 'down')
                  }
                  setIsMenuOpen(!isMenuOpen)
                }}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Tools"
              >
                <Plus className="h-5 w-5" />
              </button>

              {isMenuOpen && (
                <div className={`absolute left-0 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-[60] overflow-hidden ${
                  menuDirection === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
                }`}>
                  <button
                    type="button"
                    onClick={handleFileUpload}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Paperclip className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Add photos &amp; files</span>
                  </button>

                  {showBrandKitOption && (
                    <button
                      type="button"
                      onClick={handleBrandKitMenuClick}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <Palette className={`w-4 h-4 ${hasBrandKit ? 'text-primary-500' : 'text-gray-500 dark:text-gray-400'}`} />
                      <span className={`text-sm flex-1 ${hasBrandKit ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                        Brand Kits
                      </span>
                      {hasBrandKit && <Check className="w-4 h-4 text-primary-500" />}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Attachment file picker. Left `accept`-less so the OS dialog
                surfaces every file type — images, videos, PDFs, docs,
                spreadsheets, etc. — without the browser greying out
                non-image files. MIME routing at send time (see Home.tsx)
                buckets them into the right conversation[reference_*][]
                key based on file.type, so the BE still knows whether a
                given upload is an image, video, or audio asset. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />

            {/* Brand Kit badge (when selected, shown as a pill next to +) */}
            {selectedBrandKit && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 text-xs font-medium">
                <Palette className="w-3 h-3" />
                <span className="max-w-[120px] truncate">{selectedBrandKit.name}</span>
                {onSelectBrandKit && (
                  <button
                    type="button"
                    onClick={() => onSelectBrandKit(null)}
                    className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            <div className="flex-1" />

            {/* Send */}
            <button
              type="submit"
              disabled={!canSubmit()}
              className="flex-shrink-0 w-9 h-9 bg-primary-600 text-white rounded-full hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              title={
                isSocialAdActive
                  ? !socialAdUrl.trim()
                    ? 'Paste a social post URL to send'
                    : !isValidUrl(socialAdUrl)
                      ? 'Enter a valid http(s) URL to send'
                      : 'Send'
                  : 'Send'
              }
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Workflow chips - Gemini-style toggles below the prompt box.
          `social_ad` is a special-case chip: it doesn't set a workflow
          on the wire, it toggles the inline URL/sizes/provider panel
          above. All chips are mutually exclusive so activating one
          clears the other. */}
      {showWorkflowOptions && workflowOptions.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2.5 pt-1">
          {workflowOptions.map((opt) => {
            const isSocial = opt.key === 'social_ad'
            const isActive = isSocial
              ? isSocialAdActive
              : selectedWorkflow === (opt.key as WorkflowKey)
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  if (isSocial) {
                    setIsSocialAdActive((prev) => {
                      const next = !prev
                      if (next) setSelectedWorkflow(null)
                      return next
                    })
                  } else {
                    const key = opt.key as WorkflowKey
                    setSelectedWorkflow(isActive ? null : key)
                    if (!isActive) setIsSocialAdActive(false)
                  }
                }}
                aria-pressed={isActive}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ring-1 ring-inset transition-all ${
                  isActive
                    ? 'bg-primary-600 text-white ring-primary-600 shadow-sm'
                    : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:ring-gray-300 dark:hover:ring-gray-600 shadow-sm'
                }`}
              >
                {opt.icon}
                <span>{opt.label}</span>
                {isActive && <X className="w-3.5 h-3.5 ml-0.5 opacity-80" />}
              </button>
            )
          })}
        </div>
      )}

      {/* Brand Kit Picker carousel - rendered outside the prompt box */}
      {isBrandKitPickerOpen && showBrandKitOption && (
        <BrandKitPicker
          selectedBrandKit={selectedBrandKit ?? null}
          onSelect={(bk) => onSelectBrandKit?.(bk)}
          onClose={() => setIsBrandKitPickerOpen(false)}
          advertiserId={brandKitAdvertiserId}
        />
      )}
    </div>
  )
}
